import React from 'react';

import styles from '~/components/context-menu-list/context-menu-list.module.scss';

import { FocusTrap } from '~/utils/focus-trap';
import { Key } from '~/utils/key';

// NOTE: Jake: 2019-08-08 - WEBUI-2182
// Use a string union type for the icon class so we can avoid
// typoing icon names and instantly be able to see how many different
// icons we use in the context menu globally.
type ContextMenuIconClass =
	| 'fa-external-link-alt'
	| 'fas fa-external-link-alt'
	| 'fa-sticky-note'
	| 'fa-pencil-alt'
	| 'fa-file-excel'
	| 'fa-file-pdf'
	| 'far fa-file-excel'
	| 'fa-play'
	| 'fa-pause'
	| 'fa-plus'
	| 'fa-times'
	| 'fa-step-forward'
	| 'fa-check'
	| 'fa-exclamation'
	| 'fa-clone'
	| 'fa-history'
	| 'fa-folder-open'
	| 'fa-file-download'
	| 'fa-play-circle'
	| 'fa-eye'
	| 'fas fa-undo'
	| 'fa-undo'
	| 'fa-ban'
	| 'fa-search'
	| 'fas fa-object-group'
	| 'fa-adjust'
	| 'fa-copy'
	| 'fa-filter'
	| 'fa-check-circle'
	| 'fa-sync'
	| 'fa-hashtag'
	| 'fa-table';

export type ContextMenuEvent = 'click' | 'contextmenu' | 'keydown';

export interface ContextMenuItem {
	// Shared
	label: string;
	id?: string;
	tooltip?: string;
	iconClass?: ContextMenuIconClass;
	disabled?: boolean;

	// Button
	onClick?: (e?: MouseEvent) => void;

	// Anchor
	href?: string;
	target?: string;
}

const enum AlignLeftRight {
	Left,
	Right,
}

const enum AnchorTopBottom {
	Top,
	Bottom,
}

/**
 * An array of % widths, of the menu items in the loader.
 * menuItemCount is independent of the length of the array.
 * If it is more, the component will loop back to the start of the array.
 * These widths were chosen to make the loader look good and are not related to specific menu items.
 */
const LOADER_ITEM_PERCENTAGE_WIDTHS: number[] = [35, 60, 50, 60, 40, 55];
const DEFAULT_LOADER_ITEM_COUNT = LOADER_ITEM_PERCENTAGE_WIDTHS.length;

interface Props {
	items: ContextMenuItem[] | undefined | null;

	/**
	 * Optional loader properties.
	 * They are assigned default values in defaultProps
	 */
	/* Is the loader enabled for this menu? */
	isLoaderEnabled?: boolean;

	/* The number of items displayed in the loader */
	loaderItemCount?: number;

	/* Is the menu loading? */
	isLoading?: boolean;

	updateDirOnChange: any;
	/**
	 * onClose will trigger when an element is clicked that isn't in the scope
	 * of this element. If the close behaviour is triggered by the keyboard or other
	 * means, then `undefined` is passed in.
	 */
	onClose: (target: HTMLElement | undefined, event: ContextMenuEvent) => void;
}

interface State {
	alignLeftRight: AlignLeftRight;
	anchorTopBottom: AnchorTopBottom;
}

export class ContextMenuList extends React.Component<Props, State> {
	private static readonly alignLeftRightDefault: AlignLeftRight =
		AlignLeftRight.Left;

	private static readonly anchorTopBottomDefault: AnchorTopBottom =
		AnchorTopBottom.Bottom;

	private static readonly anchorTargetDefault = '_blank';

	private refListEl = React.createRef<HTMLUListElement>();

	private focusTrap: FocusTrap | undefined;

	/**
	 * closeHandle ensures we don't trigger onClose() multiple times when it
	 * should only trigger once.
	 */
	private closeHandle: number = 0;

	constructor(props) {
		super(props);

		this.state = {
			alignLeftRight: ContextMenuList.alignLeftRightDefault,
			anchorTopBottom: ContextMenuList.anchorTopBottomDefault,
		};
	}

	static defaultProps = {
		isLoaderEnabled: false,
		loaderItemCount: DEFAULT_LOADER_ITEM_COUNT,
		isLoading: false,
	};

	public componentDidMount(): void {
		this.tryInitFocusTrap();
		this.updateTooltip();
		this.calculateListDirection();
		document.body.addEventListener('click', this.onClickBody);
		document.body.addEventListener('keydown', this.onKeyDown, {
			capture: true,
			passive: false,
		});
		// NOTE: Jake: 2019-02-19 - WEBUI-308
		// We queue this up on the next frame so that it's not inclusive
		// of the right-click event we just fired off. Without this logic, it's
		// possible to open two or more context menus across various objects at
		// the same time.
		window.requestAnimationFrame(() => {
			document.body.addEventListener(
				'contextmenu',
				this.onContextMenuBody,
			);
		});
	}

	public componentWillUnmount(): void {
		if (this.focusTrap) {
			this.focusTrap.disable();
			this.focusTrap = undefined;
		}

		document.body.removeEventListener('click', this.onClickBody);
		document.body.removeEventListener('keydown', this.onKeyDown, true);
		document.body.removeEventListener(
			'contextmenu',
			this.onContextMenuBody,
		);
	}

	public componentDidUpdate(
		prevProps: Props,
		prevState: State,
		snapshot,
	): void {
		// NOTE: Jake: 2019-10-02
		// Used to use Aurelia:
		// itemsChanged(items: ContextMenuItem[], oldItems: ContextMenuItem[])
		const hasItemsChanged =
			this.props.items !== prevProps.items ||
			this.props.isLoading !== prevProps.isLoading;
		if (hasItemsChanged) {
			this.updateTooltip();
		}

		if (
			hasItemsChanged ||
			this.props.updateDirOnChange !== prevProps.updateDirOnChange
		) {
			// Update direction
			this.calculateListDirection();
		}

		// NOTE: Jake: 2019-08-12 - WEBUI-2182
		// Ensure focus is kept when context menu position changes
		this.tryInitFocusTrap();
	}

	/**
	 * Get the width of a menu item as a percentage string
	 * @param index - Zero based value corresponding to the position of a menu item in the list.
	 */
	private getWidth(index: number): string {
		const normalisedIdx = index % DEFAULT_LOADER_ITEM_COUNT;
		return LOADER_ITEM_PERCENTAGE_WIDTHS[normalisedIdx] + '%';
	}

	/**
	 * Should the loader be rendered now?
	 */
	private shouldRenderLoader(): boolean {
		return (
			this.props.isLoaderEnabled &&
			(this.props.isLoading ||
				!this.props.items ||
				this.props.items.length === 0)
		);
	}

	/**
	 * Get JSX for the list items in the skeleton loader
	 */
	private renderLoaderItems(): JSX.Element[] {
		const loaderItems = [];
		for (let i = 0; i < this.props.loaderItemCount; i++) {
			loaderItems.push(
				<li
					className={styles['skeleton-menu-item']}
					key={'loader-menu-item-' + i}
				>
					<div className={styles['skeleton-menu-item-icon']} />
					<div
						className={styles['skeleton-menu-item-description']}
						style={{ width: `${this.getWidth(i)}` }}
					/>
				</li>,
			);
		}
		return loaderItems;
	}

	private tryInitFocusTrap(): void {
		if (this.focusTrap) {
			this.focusTrap.disable();
			this.focusTrap = undefined;
		}
		const refListEl = this.refListEl.current;
		if (!refListEl) {
			return;
		}

		// Enable focus trap
		this.focusTrap = new FocusTrap(refListEl, {
			clickOutsideDeactivates: true,
		});
		if (this.focusTrap.canEnable()) {
			this.focusTrap.enable();
		}

		// Focus first element (if not already focused)
		ContextMenuList.tryFocusFirstElementIfNotFocusedInside(
			this.refListEl.current,
		);
	}

	private static tryFocusFirstElementIfNotFocusedInside(
		container: HTMLElement,
	): void {
		if (
			!container ||
			// Ignore if we are already focused on an element inside this component.
			container.contains(document.activeElement)
		) {
			return;
		}
		const elements = FocusTrap.getTabbableNodes(container);
		if (elements.length === 0) {
			return;
		}
		elements[0].focus();
	}

	private computeTooltip(menuItem: ContextMenuItem): string | undefined {
		if (!menuItem.tooltip) {
			return undefined;
		}
		let tooltip = menuItem.tooltip;
		tooltip = tooltip.replace('{{label}}', menuItem.label);
		if (!tooltip) {
			return undefined;
		}
		return tooltip;
	}

	private computeHref(menuItem: ContextMenuItem): string {
		const { href } = menuItem;
		return href ? href : '';
	}

	private onKeyDown = (e: KeyboardEvent): void => {
		const refListEl = this.refListEl.current;
		if (!refListEl) {
			return;
		}
		if (e.key === Key.ESCAPE) {
			this.closeMenu(undefined, 'keydown');
			return;
		}
		if (e.key !== Key.ARROW_DOWN && e.key !== Key.ARROW_UP) {
			return;
		}
		const items = FocusTrap.getTabbableNodes(refListEl);
		if (items.length === 0) {
			return;
		}

		// Find position in list
		let currentIndex: number = -1;
		for (let i = 0; i < items.length; i++) {
			const el = items[i];
			if (el === document.activeElement) {
				currentIndex = i;
				break;
			}
		}
		if (currentIndex === -1) {
			// If no element is focused
			return;
		}

		// Get element to focus
		let nextElToFocus: HTMLElement;
		switch (e.key) {
			case Key.ARROW_UP:
				if (currentIndex - 1 >= 0) {
					nextElToFocus = items[currentIndex - 1];
				}
				break;
			case Key.ARROW_DOWN:
				if (currentIndex + 1 < items.length) {
					nextElToFocus = items[currentIndex + 1];
				}
				break;
		}
		if (!nextElToFocus) {
			return;
		}
		nextElToFocus.focus();
	};

	private onClickBody = (e: MouseEvent): void => {
		if (this.shouldIgnoreEvent(e)) {
			return;
		}
		const target = e.target as HTMLElement;
		this.closeMenu(target, 'click');
	};

	private onContextMenuBody = (e: MouseEvent): void => {
		if (this.shouldIgnoreEvent(e)) {
			return;
		}
		const target = e.target as HTMLElement;
		this.closeMenu(target, 'contextmenu');
	};

	private shouldIgnoreEvent = (e: MouseEvent): boolean => {
		// NOTE: Jake: 2019-11-15 - WEBUI-2690
		// If this component doesn't exist or isn't visible on screen, avoid firing.
		// This stops this firing ~50 times per right-click as each form field
		// is registering this event.
		// ie. node.offsetParent === null is the cheapest way to see if "display: none".
		if (
			!this.refListEl.current ||
			this.refListEl.current.offsetParent === null
		) {
			return true;
		}
		const target = e.target as HTMLElement;
		if (
			target &&
			this.refListEl.current &&
			this.refListEl.current.contains(target) &&
			!ContextMenuList.hasButtonOrAnchorAncestor(target)
		) {
			// Don't close context menu if
			// - We're clicking on or inside the context menu
			// - Not clicking a <button>/<a> inside the context menu
			return true;
		}
		return false;
	};

	private closeMenu = (
		target: HTMLElement | undefined,
		event: ContextMenuEvent,
	): void => {
		// NOTE: Jake: 2019-02-20 - WEBUI-308
		// We wait 1 frame and then hide the context menu.
		// This allows click events on context menu items to fire before
		// closing the menu.
		if (this.closeHandle === 0) {
			this.closeHandle = window.requestAnimationFrame(() => {
				this.props.onClose(target, event);
				this.closeHandle = 0;
			});
		}
	};

	private onClickMenuButton(
		e: React.MouseEvent,
		menuItem: ContextMenuItem,
	): void {
		if (!menuItem.onClick) {
			throw new Error(
				'Missing onClick() on menu item: ' + menuItem.label,
			);
		}
		if (menuItem.disabled) {
			// Ignore if disabled
			return;
		}
		menuItem.onClick(e.nativeEvent);
	}

	/**
	 * onKeyDownAnchor adds ENTER and SPACE key support to <a> tags so they behave the same
	 * as <button>'s in the context menu.
	 */
	private onKeyDownAnchor(
		e: React.KeyboardEvent,
		menuItem: ContextMenuItem,
	): void {
		if (
			(e.key === Key.ENTER || e.key === Key.SPACE) &&
			e.target instanceof HTMLElement
		) {
			e.target.click();
		}
	}

	private calculateListDirection(): void {
		const refListEl = this.refListEl.current;
		if (!refListEl) {
			return;
		}
		const rect = refListEl.getBoundingClientRect();
		if (rect.bottom === 0) {
			// If element is "display: none" or has no height
			return;
		}

		const viewportHeight =
			window.innerHeight || document.documentElement.clientHeight;
		if (rect.top < 0) {
			// if top of context menu is above top of viewport, always want menu to open
			// down otherwise scrollbar will not appear and you cannot reach top of menu
			this.setState({ anchorTopBottom: AnchorTopBottom.Bottom });
		} else if (rect.bottom > viewportHeight && rect.height < rect.top) {
			// only open context menu up if bottom of menu is below bottom of viewport *and* height of menu is
			// less than current top of menu so top of menu does not extend above top of viewport after this change
			this.setState({ anchorTopBottom: AnchorTopBottom.Top });
		} else if (rect.bottom < 0) {
			this.setState({ anchorTopBottom: AnchorTopBottom.Bottom });
		}

		const viewportWidth =
			window.innerWidth || document.documentElement.clientWidth;
		if (rect.right > viewportWidth) {
			this.setState({ alignLeftRight: AlignLeftRight.Right });
		} else if (rect.left < 0) {
			this.setState({ alignLeftRight: AlignLeftRight.Left });
		}
	}

	private updateTooltip() {
		const refListEl = this.refListEl.current;
		if (!refListEl) {
			return;
		}

		// NOTE: Jake: 2019-08-12 - WEBUI-2182
		// Query all elements that can have "[data-context-tooltip]" attribute set.
		for (const el of refListEl.querySelectorAll('li,a,button')) {
			// NOTE: Jake: 2019-08-14 - WEBUI-2182
			// Dispose tooltips (to handle items that have since removed their tooltip)
			if (el.getAttribute('data-original-title')) {
				el.removeAttribute('data-original-title');
				$(el).tooltip('dispose');
			}

			// NOTE: Jake: 2019-08-14 - WEBUI-2182
			// Update tooltip if "tooltip" property is set. This is used by
			// the <acurity-table> context menu
			const tooltip = el.getAttribute('data-context-tooltip');
			if (tooltip) {
				el.setAttribute('data-original-title', tooltip);
			}
			if (!el.getAttribute('data-original-title')) {
				continue;
			}
			$(el).tooltip({
				delay: {
					show: 500,
					hide: 100,
				},
			});
		}
	}

	private renderButtonInner(menuItem: ContextMenuItem): JSX.Element {
		return (
			<React.Fragment>
				{menuItem.iconClass && (
					<i
						className={[
							styles.icon,
							'fas',
							menuItem.iconClass,
						].join(' ')}
						aria-hidden="true"
					/>
				)}
				<span className={styles.label}>{menuItem.label}</span>
			</React.Fragment>
		);
	}

	private static hasButtonOrAnchorAncestor(element: HTMLElement): boolean {
		while (element) {
			if (element.tagName === 'BUTTON' || element.tagName === 'A') {
				return true;
			}
			element = element.parentElement;
		}
		return false;
	}

	public render() {
		const { alignLeftRight, anchorTopBottom } = this.state;
		const { items } = this.props;
		return (
			<nav
				className={[
					styles.nav,
					alignLeftRight === AlignLeftRight.Right
						? styles['nav--align-right']
						: '',
					alignLeftRight === AlignLeftRight.Left
						? styles['nav--align-left']
						: '',
					anchorTopBottom === AnchorTopBottom.Bottom
						? styles['nav--anchor-bottom']
						: '',
					anchorTopBottom === AnchorTopBottom.Top
						? styles['nav--anchor-top']
						: '',
				].join(' ')}
				ref={this.refListEl}
			>
				<ul
					className={
						styles.list +
						`${
							this.shouldRenderLoader()
								? ' ' + styles['skeleton-menu-container']
								: ''
						}`
					}
				>
					{items &&
						!this.shouldRenderLoader() &&
						items.map((menuItem, index) => {
							// NOTE: Jake: 2019-08-09 - WEBUI-2182
							// We might want to compute an auto-generated unique ID for accessibility later
							// if the "id" property is not set.
							const id = menuItem.id ? menuItem.id : undefined;
							const isDisabled =
								menuItem.disabled || menuItem.href === '';
							const tooltip = this.computeTooltip(menuItem);
							const className = [
								styles.clickable,
								isDisabled ? styles['clickable--disabled'] : '',
							].join(' ');
							return (
								<li
									className={[
										isDisabled
											? styles['item--disabled']
											: '',
									].join(' ')}
									// NOTE: Jake: 2019-10-21 - WEBUI-2566
									// To make tooltips appear on disabled items the "best practice" is to
									// put the tooltip on the wrapping element.
									data-context-tooltip={
										isDisabled ? tooltip : undefined
									}
									// NOTE: Jake: 2019-10-03
									// Using "index" is a last resort if your data does not have
									// unique ID. Be wary when using this as an example
									key={index}
								>
									{(menuItem.onClick || isDisabled) && (
										<button
											id={id}
											type="button"
											onClick={(e: React.MouseEvent) =>
												this.onClickMenuButton(
													e,
													menuItem,
												)
											}
											className={className}
											disabled={isDisabled}
											data-context-tooltip={tooltip}
										>
											{this.renderButtonInner(menuItem)}
										</button>
									)}
									{menuItem.href && !isDisabled && (
										<a
											id={id}
											href={this.computeHref(menuItem)}
											onKeyDown={(
												e: React.KeyboardEvent,
											) =>
												this.onKeyDownAnchor(
													e,
													menuItem,
												)
											}
											className={className}
											target={
												menuItem.target
													? menuItem.target
													: ContextMenuList.anchorTargetDefault
											}
											data-context-tooltip={tooltip}
										>
											{this.renderButtonInner(menuItem)}
										</a>
									)}
								</li>
							);
						})}
					{this.shouldRenderLoader() && this.renderLoaderItems()}
				</ul>
			</nav>
		);
	}
}
export default React.memo(ContextMenuList, arePropsEqual);

function arePropsEqual(oldProps: Props, newProps: Props) {
	const menuListEqual = areItemsEqual(oldProps.items, newProps.items);
	return (
		oldProps.isLoaderEnabled === newProps.isLoaderEnabled &&
		oldProps.isLoading === newProps.isLoading &&
		oldProps.updateDirOnChange === newProps.updateDirOnChange &&
		oldProps.loaderItemCount === newProps.loaderItemCount &&
		menuListEqual
	);
}

function areItemsEqual(
	oldItems: ContextMenuItem[] | undefined | null,
	newItems: ContextMenuItem[] | undefined | null,
): boolean {
	if (oldItems?.length !== newItems?.length) return false;
	for (const k of Object.keys(oldItems)) {
		if (oldItems[k].label !== newItems[k]?.label) return false;
	}
	return true;
}
